import React, { useState } from 'react';
import { formatRelativeTime, formatDateUser, formatDateTimeUser } from '../../utils/dateUtils';
import { getTaskStatusColor, getPriorityColor, getPriorityIcon, getTaskTypeIcon } from '../../utils/taskUtils';
import './TaskCard.css';

const formatDateTimeNoYearLocal = (value) => {
  if (!value) return '';
  try {
    const d = new Date(value);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return String(value);
  }
};

const formatDateNoYearLocal = (value) => {
  if (!value) return '';
  try {
    const d = new Date(value);
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return String(value);
  }
};

const TaskCard = ({ 
  task, 
  isExpanded, 
  isSelected, 
  onTaskClick, 
  onImageClick,
  onSubtaskClick,
  onActionClick,
  onCreateSubtask,
  departments = [],
  employees = [],
  employeeLocations = [],
  canModify = false
}) => {
  const [isImageLoading, setIsImageLoading] = useState({});
  const statusColor = getTaskStatusColor(task.status);

  const truncateText = (text, maxLength = 160) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength - 1).trim()}…`;
  };

  const linkifyText = (text) => {
    if (!text) return '';
    const parts = text.split(/(https?:\/\/[^\s]+)/gi);
    return parts.map((part, index) => {
      if (!part) return null;
      if (/^(https?:\/\/[^\s]+)$/i.test(part)) {
        return (
          <a
            key={`link-${index}`}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {part}
          </a>
        );
      }
      return <span key={`text-${index}`}>{part}</span>;
    });
  };

  const getDepartmentName = () => {
    if (task.department?.name) return task.department.name;
    if (task.department) return task.department;
    return null;
  };

  const truncatedDescription = truncateText(task.description);
  const renderedDescription = linkifyText(truncatedDescription);
  const subtaskCount = Array.isArray(task.subtasks) ? task.subtasks.length : 0;
  const completionPercent = Number.isFinite(task.completionPercentage) ? Math.max(0, Math.min(100, task.completionPercentage)) : 0;

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onTaskClick(task);
    }
  };

  const handleImageLoad = (imageIndex) => {
    setIsImageLoading(prev => ({ ...prev, [imageIndex]: false }));
  };

  const handleImageError = (imageIndex) => {
    setIsImageLoading(prev => ({ ...prev, [imageIndex]: false }));
  };

  const extractMeetLinkFromText = (text) => {
    if (!text || typeof text !== 'string') return null;
    const meetRegex = /(https?:\/\/(?:meet\.google\.com|hangouts\.google\.com|calendar\.google\.com\/event[^\s]*|zoom\.us\/[^\s]+|teams\.microsoft\.com\/[^\s]+)[^\s]*)/i;
    const match = text.match(meetRegex);
    return match ? match[0] : null;
  };

  const meetingLink =
    task?.meetingLink ||
    task?.meetingURL ||
    task?.meetingUrl ||
    task?.meetUrl ||
    task?.hangoutLink ||
    task?.calendar?.hangoutLink ||
    task?.meeting?.hangoutLink ||
    extractMeetLinkFromText(task?.description);

  return (
    <div 
      className={`task-card ${isExpanded ? 'expanded' : ''} ${isSelected ? 'selected' : ''}`}
      onClick={() => onTaskClick(task)}
      onKeyDown={handleKeyPress}
      tabIndex={0}
      role="button"
      aria-expanded={isExpanded}
      aria-label={`Task: ${task.description}`}
    >
      <div className="task-header">
        <div className="task-main-info">
          <div className="task-title-row">
           
            <div className="task-badges">
            {task.taskType && (
                <span 
                  className="task-type-badge" 
                  title={`Type: ${task.taskType}`}
                  aria-label={`Type: ${task.taskType}`}
                >
                  {task.taskType == 'customer' ? 'Customer Request' :  task.taskType} #{task.id}
                </span>
              )}
              {task.priority && (
                <span 
                  className={`priority-badge priority-${task.priority.toLowerCase()}`}
                  title={`Priority: ${task.priority}`}
                  aria-label={`Priority: ${task.priority}`}
                >
                  {task.priority}
                </span>
              )}
              {getDepartmentName() && (
                <span 
                  className="team-badge"
                  title={`Team: ${getDepartmentName()}`}
                  aria-label={`Team: ${getDepartmentName()}`}
                >
                  {getDepartmentName()}
                </span>
              )}
             
            </div>
          </div>
          <p className="task-description" title={task.description}>{renderedDescription}</p>
          {typeof task?.taskType === 'string' && task.taskType.toLowerCase() === 'meeting' && meetingLink && (
            <div className="task-meta-compact" style={{ marginTop: 4 }}>
              <div className="meta-item">
                <span className="meta-label">Meeting</span>
                <a
                  href={meetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ textDecoration: 'underline', marginLeft: 4 }}
                >
                  <strong>Join</strong>
                </a>
              </div>
            </div>
          )}
          <div className="task-meta-compact">
            <div className="meta-item">
              <span className="meta-label">Customer</span>
              <span className="meta-value">{task.customer?.name || 'N/A'}</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">Created</span>
              <span className="meta-value">{task.createdAt ? formatDateTimeUser(task.createdAt) : '-'}</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">Due</span>
              <span className="meta-value">{task.dueDate ? formatDateTimeNoYearLocal(task.dueDate) : '-'}</span>
            </div>
            {task.followUpOn && (
              <div className="meta-item">
                <span className="meta-label">Follow up</span>
                <span className="meta-value">{formatDateUser(task.followUpOn)}</span>
              </div>
            )}
            <div className="meta-item">
              <span className="meta-label">Subtasks</span>
              <span className="meta-value">{subtaskCount}</span>
            </div>
          </div>
          <div className="completion-progress" style={{ marginTop: 6 }}>
            <div 
              className="progress-bar" 
              role="progressbar" 
              aria-valuenow={completionPercent} 
              aria-valuemin="0" 
              aria-valuemax="100"
              style={{ height: 4 }}
            >
              <div 
                className="progress-fill" 
                style={{ width: `${completionPercent}%` }}
              />
            </div>
            <span className="progress-text" style={{ fontSize: 10 }}>{completionPercent}%</span>
          </div>
        </div>
        <div className="task-actions">
          
          <span 
            className="task-status" 
            style={{ backgroundColor: statusColor }}
            aria-label={`Status: ${task.status.replace('-', ' ')}`}
          >
            {task.status.replace('-', ' ')}
          </span>
          {canModify && (task.status === 'incoming' || !['resolved', 'rejected', 'incoming'].includes(task.status)) && !isExpanded && (
            <div className="task-action-buttons">
              <button 
                className="action-button reject"
                onClick={(e) => {
                  e.stopPropagation();
                  onActionClick(task, 'reject');
                }}
                aria-label={`Reject task: ${task.description}`}
              >
                Reject
              </button>
              <button 
                className={`action-button ${['pending','incoming'].includes(String(task.status).toLowerCase()) ? 'start' : 'resolve'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onActionClick(task, 'resolve');
                }}
                aria-label={`Resolve task: ${task.description}`}
              >
                {(task.status === 'pending' || task.status === 'incoming') ? 'Start' : 'Complete'}
              </button>
              <button 
                className="action-button assign"
                onClick={(e) => {
                  e.stopPropagation();
                  onActionClick(task, 'assign-both');
                }}
                aria-label={`${task.status === 'incoming' ? 'Assign' : 'Reassign'} task: ${task.description}`}
              >
                {task.status === 'incoming' ? 'Assign' : 'Reassign'}
              </button>
             
            </div>
          )}
          {canModify && onCreateSubtask && !['resolved', 'rejected'].includes(task.status) && !isExpanded && (
            <div className="task-action-buttons create-subtask-section">
              <button 
                className="action-button create-subtask"
                onClick={(e) => {
                  e.stopPropagation();
                  onCreateSubtask(task);
                }}
                aria-label={`Create subtask for: ${task.description}`}
              >
                Create Subtask
              </button>
            </div>
          )}
        </div>
      </div>
      
      {isExpanded && (
        <TaskCardDetails 
          task={task}
          onImageClick={onImageClick}
          onSubtaskClick={onSubtaskClick}
          isImageLoading={isImageLoading}
          setIsImageLoading={setIsImageLoading}
          handleImageLoad={handleImageLoad}
          handleImageError={handleImageError}
          departments={departments}
          employees={employees}
          employeeLocations={employeeLocations}
          onCreateSubtask={onCreateSubtask}
        />
      )}
    </div>
  );
};

const TaskCardDetails = ({ 
  task, 
  onImageClick, 
  onSubtaskClick, 
  isImageLoading, 
  setIsImageLoading,
  handleImageLoad,
  handleImageError,
  departments = [],
  employees = [],
  employeeLocations = [],
  onCreateSubtask
}) => {
  const handleSubtaskKeyPress = (event, subtask) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      onSubtaskClick(subtask);
    }
  };

  const handleImageKeyPress = (event, img) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onImageClick(img);
    }
  };

  return (
    <div className="task-details">
      <div className="task-details-grid">
       

        {/* Basic Information and Assignment - Side by Side Row */}
        <div className="info-assignment-row">
          <div className="detail-section">
            <h4 className="section-title">Task Information</h4>
            <div className="detail-rows">
              <div className="detail-row">
               {/*  <div className="detail-item">
                  <span className="detail-label">Priority:</span>
                  <span className={`detail-value priority-badge priority-${task.priority?.toLowerCase()}`}>
                    {task.priority}
                  </span>
                </div> */}
               {/*  <div className="detail-item">
                  <span className="detail-label">Type:</span>
                  <span className="detail-value">{task.taskType}</span>
                </div> */}
                {/* <div className="detail-item">
                  <span className="detail-label">Progress:</span>
                  <span className="detail-value">{task.completionPercentage}%</span>
                </div> */}
                
              </div>
              {task.dueDate && (
                <div className="detail-row">
                  <div className="detail-item">
                    <span className="detail-label">Due Date:</span>
                    <span className="detail-value">{formatDateTimeNoYearLocal(task.dueDate)}</span>
                  </div>
                  {task.startedAt && (
                    <div className="detail-item">
                      <span className="detail-label">Started:</span>
                      <span className="detail-value">{formatDateTimeNoYearLocal(task.startedAt)}</span>
                    </div>
                  )}
                  {task.resolvedAt && (
                    <div className="detail-item">
                      <span className="detail-label">Resolved:</span>
                      <span className="detail-value">{formatDateTimeNoYearLocal(task.resolvedAt)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="detail-section">
            <h4 className="section-title">Customer</h4>
            <div className="detail-rows">
              <div className="detail-row">
                <div className="detail-item">
                  <span className="detail-label">Customer:</span>
                  <span className="detail-value">{task.customer?.name}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{task.customer?.email}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Phone:</span>
                  <span className="detail-value">{task.customer?.phone}</span>
                </div>
              </div>
             
            </div>
          </div>
        </div>

          {/* People & Assignment Section */}
          <div className="detail-section">
            <h4 className="section-title">Assignment</h4>
            <div className="detail-rows">
             
              <div className="detail-row">
               
                <div className="detail-item">
                  <span className="detail-label">Field Staff:</span>
                  <span className="detail-value">
                    {task.assignedEmployee ? `${task.assignedEmployee.firstName} ${task.assignedEmployee.lastName}` : 'Unassigned'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Supervisor:</span>
                  <span className="detail-value">
                    {task.supervisor ? `${task.supervisor.firstName} ${task.supervisor.lastName}` : 'Unassigned'}
                  </span>
                </div>
              </div>
              <div className="detail-row">
              {task.supervisorNotes && (
                                <div className="detail-item">

                    <span className="detail-label">Supervisor Notes:</span>
                    <span className="detail-value">{task.supervisorNotes}</span>
                    </div>
                
              )}
              {task.employeeNotes && (
              
                  <div className="detail-item">
                    <span className="detail-label">Staff Notes:</span>
                    <span className="detail-value">{task.employeeNotes}</span>
                  </div>
               
              )}
            </div>
            </div>
          </div>

         

        {/* Location & Map Row */}
        {/* Location section removed intentionally to simplify task card */}

        {/* Subtasks Section */}
        {task.subtasks && task.subtasks.length > 0 && (
          <div className="detail-section">
            <h4 className="section-title">Subtasks ({task.subtasks.length})</h4>
            <div className="subtasks-list">
              {task.subtasks.map((subtask, index) => (
                <div 
                  key={subtask.id} 
                  className="subtask-item clickable"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSubtaskClick(subtask);
                  }}
                  onKeyDown={(e) => handleSubtaskKeyPress(e, subtask)}
                  tabIndex={0}
                  role="button"
                  aria-label={`Subtask ${index + 1}: ${subtask.description}`}
                >
                  <div className="subtask-header">
                    <span className="subtask-number">#{index + 1}</span>
                    <span className="subtask-description">{subtask.description}</span>
                    <span 
                      className="subtask-status" 
                      style={{ backgroundColor: getTaskStatusColor(subtask.status) }}
                      aria-label={`Status: ${subtask.status.replace('-', ' ')}`}
                    >
                      {subtask.status.replace('-', ' ')}
                    </span>
                    {subtask.priority && (
                      <span 
                        className={`subtask-priority priority-badge priority-${subtask.priority.toLowerCase()}`}
                        aria-label={`Priority: ${subtask.priority}`}
                      >
                        {subtask.priority}
                      </span>
                    )}
                  </div>
                  <div className="subtask-meta">
                    <span><strong>Department:</strong> {subtask.department?.name}</span>
                    {subtask.dueDate && (
                      <span><strong>Due:</strong> {formatDateTimeNoYearLocal(subtask.dueDate)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Images Section */}
        {((task.images && task.images.length > 0) || (task.employeeimages && task.employeeimages.length > 0)) && (
          <div className="detail-section">
            <h4 className="section-title">Images</h4>
            {task.images && task.images.length > 0 && (
              <div className="images-subsection">
                <h5 className="subsection-title">Customer Images:</h5>
                <div className="image-grid">
                  {task.images.map((img, idx) => (
                    <div key={idx} className="image-container">
                      {isImageLoading[`customer-${idx}`] && (
                        <div className="image-loading">Loading...</div>
                      )}
                      <img 
                        src={img} 
                        alt={`Customer ${idx + 1}`} 
                        className="task-image"
                        onClick={() => onImageClick(img)}
                        onKeyDown={(e) => handleImageKeyPress(e, img)}
                        onLoad={() => handleImageLoad(`customer-${idx}`)}
                        onError={() => handleImageError(`customer-${idx}`)}
                        tabIndex={0}
                        role="button"
                        aria-label={`View customer image ${idx + 1}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {task.employeeimages && task.employeeimages.length > 0 && (
              <div className="images-subsection">
                <h5 className="subsection-title">Employee Images:</h5>
                <div className="image-grid">
                  {task.employeeimages.map((img, idx) => (
                    <div key={idx} className="image-container">
                      {isImageLoading[`employee-${idx}`] && (
                        <div className="image-loading">Loading...</div>
                      )}
                      <img 
                        src={img} 
                        alt={`Employee ${idx + 1}`} 
                        className="task-image"
                        onClick={() => onImageClick(img)}
                        onKeyDown={(e) => handleImageKeyPress(e, img)}
                        onLoad={() => handleImageLoad(`employee-${idx}`)}
                        onError={() => handleImageError(`employee-${idx}`)}
                        tabIndex={0}
                        role="button"
                        aria-label={`View employee image ${idx + 1}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskCard; 